import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { cn } from '@poseidon/components/cn';
import { Button, ButtonGroup } from '@poseidon/components/Input2';
import { Development, General, Layout, Time, Users } from '@poseidon/components/StrokeIcons';
import { TemplateBlockDelayEditPure } from '@poseidon/components/TemplateBlockCard';

import type { TouchFlowContact } from './ContactFacePile';
import type { TouchGraphModel } from './touchGraphLayout';
import { CandidateFlowGraph } from '~/components/CandidateFlowGraph/CandidateFlowGraph';
import { ContactFacePile } from './ContactFacePile';
import { formatWaitLabel, MAX_FORK_ARMS, TOUCH_CANVAS_PAD, touchStepsAreTerminal } from './touchGraphLayout';
import { MinimalAddTile } from './MinimalAddTile';
import { TouchAutoWithdrawChip } from './TouchAutoWithdrawChip';
import { TouchNodeCard } from './TouchNodeCard';

const MIN_SCALE = 0.25;
const MAX_SCALE = 2;
const DAY_MS = 24 * 60 * 60 * 1000;
/** Inner padding when rendered embedded — trims the canvas's outer TOUCH_CANVAS_PAD. */
const EMBED_MARGIN = 12;
/**
 * Side gap kept between the inline editor and the viewport edges when it's clamped
 * to fit. Matches the scroll-into-view `margin` below so a fitted editor can always
 * be panned fully on-screen.
 */
const EDITOR_FIT_MARGIN = 24;
/** Floor for the clamped editor width — narrower than this cramps the form. */
const EDITOR_MIN_W = 240;
/** Gap between a touch card's right edge and its enrolled-contact face pile. */
const FACE_GAP = 8;

/**
 * Natural rendered size of the graph when `embedded` — the outer canvas padding is
 * trimmed on both axes (the content bounds already fit every node/edge). Consumers
 * (e.g. the campaign switch) use this to size the container.
 */
export function embeddedTouchGraphSize(model: TouchGraphModel): { w: number; h: number } {
  return {
    w: model.contentW - 2 * TOUCH_CANVAS_PAD + 2 * EMBED_MARGIN,
    h: model.contentH - 2 * TOUCH_CANVAS_PAD + 2 * EMBED_MARGIN,
  };
}
/** Half-height of the connector break that frames the (centered) wait chip. */
const EDGE_GAP_HALF = 17;
/** Stop the arrowhead just short of the node so it doesn't poke into the card. */
const ARROW_INSET = 5;
/** Stem below the connect touch before the not-accepted arm turns left. */
const FORK_STEM = 28;
/** Where the not-accepted arm exits the connect card's bottom edge (px from its left). */
const FORK_LEFT_EXIT = 44;
/** Width of the (invisible) hover band over a connector — reveals its insert "+". */
const EDGE_HOVER_W = 260;
const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), hi);

/**
 * The not-accepted arm: exits the connect card's bottom-LEFT (a separate point
 * from the green accepted arm at the bottom-center), drops a short stem, then
 * elbows left and down into the No column.
 */
function forkPath(
  parent: { x: number; y: number; w: number; h: number },
  targetCx: number,
  targetTopY: number,
): string {
  const parentCx = parent.x + parent.w / 2;
  // Exit toward the target's side: left for a No branch, right for a parallel arm.
  // Clamp the inset to half the card so narrow (minimal) cards exit near center
  // instead of past their own edge.
  const exit = Math.min(FORK_LEFT_EXIT, parent.w / 2);
  const startX = targetCx >= parentCx ? parent.x + parent.w - exit : parent.x + exit;
  const pb = parent.y + parent.h;
  const forkY = pb + FORK_STEM;
  return `M ${startX} ${pb} L ${startX} ${forkY} L ${targetCx} ${forkY} L ${targetCx} ${targetTopY - ARROW_INSET}`;
}

/**
 * An editor rendered inline on the canvas, anchored to the touch it edits (or
 * the add/insert slot for a new touch). Positioned in content space so it sits
 * exactly where the touch is; opening one snaps the view to 100% and centers it.
 */
export type InlineEditor = {
  /** Stable identity of the open editor — changing it re-focuses the view. */
  key: string;
  /** Spine center x + top y (content space) the editor anchors to. */
  cx: number;
  y: number;
  w: number;
  /** Hide this node card while its editor is open (edit mode). */
  hideBlockId?: string;
  /** Hide the trailing add-node while creating a new trailing touch. */
  hideAdd?: boolean;
  /** Hide a specific fork arm's trailing add-node (its editor replaces it); others stay. */
  hideArmTailBlockId?: string;
  children: ReactNode;
};

interface Props {
  model: TouchGraphModel;
  selectedStepId: string | null;
  onSelectStep: (stepId: string) => void;
  onDeleteBlock: (blockId: string) => void;
  /**
   * Add another step to an existing touch (same day). Still accepted by the parent,
   * but the graph no longer surfaces a dedicated affordance for it — new steps are
   * added via the central insert "+" between touches.
   */
  onAddStep?: (blockId: string, lastStepId: string) => void;
  /** Start a connect touch's not-accepted (No) branch from its stub — (connect blockId, wfcId). */
  onAddNoBranchStep: (blockId: string, wfcId: string) => void;
  /**
   * Fork a branch off a touch: after its step group finishes, start a new
   * independent arm (a new "day" under `blockId`). Optional — the affordance only
   * renders when provided.
   */
  onAddForkArm?: (blockId: string) => void;
  /**
   * Append another (day-delayed) step to the TAIL of a branch arm (`blockId` is the
   * arm's last block). Lets an arm grow into a multi-step sequence. Optional.
   */
  onAddArmStep?: (blockId: string) => void;
  /** Append a touch after the last one. */
  onAddTouch: () => void;
  /** Insert a touch before the block with this id (the "+" between two touches). */
  onInsertBefore: (blockId: string) => void;
  /** Change the wait (delay) leading into a block — `days` is raw days, `alignWeek` marks weeks. */
  onChangeWait: (blockId: string, days: number, alignWeek: boolean) => void | Promise<void>;
  /**
   * Toggle a connect step's auto-withdraw flag (withdraw the still-pending invite
   * once its not-accepted branch resolves). Optional — omit to render the chip
   * read-only (e.g. surfaces that shouldn't edit the template).
   */
  onChangeAutoWithdraw?: (connectStepId: string, value: boolean) => void | Promise<void>;
  /** Label for the trailing add-node ("Add a touch" vs "Add the first touch"). */
  addLabel: string;
  /** The inline editor to render on the canvas, or null when nothing is open. */
  inlineEditor?: InlineEditor | null;
  /** The candidate → enrollment pipeline header shown above the first touch. */
  candidates?: {
    expanded: boolean;
    onToggle: () => void;
    total: number;
    errorCount: number;
    counts: Partial<Record<string, number>> | undefined;
    hasFilterRules: boolean;
    hasPersonalizedSteps: boolean;
  } | null;
  /**
   * Render statically at natural content size (no pan/zoom, no zoom controls, no
   * dot-grid) — for embedding the graph inside another surface (e.g. a campaign
   * switch destination). The graph is still fully interactive/editable.
   */
  embedded?: boolean;
  /** Render each touch as a compact headerless icon card (Minimal) instead of a full card. */
  minimal?: boolean;
  /**
   * Complete an action-required step for the contact this graph is rendered for
   * (passed the contact's own cadence-step id). Contact page only — omit on the
   * template page so no mark-done button renders.
   */
  onMarkStepFinished?: (cadenceStepId: string) => void;
  /** Toggle handler for the Minimal / Expanded control. Omit (with showViewToggle off) to hide it. */
  onSetMinimal?: (minimal: boolean) => void;
  /** Render the Minimal / Expanded toggle overlay (bottom-left). */
  showViewToggle?: boolean;
  /**
   * Enrolled-contact face piles keyed by node blockId — the "show contacts in this
   * flow" overlay. Null (overlay off) renders no piles.
   */
  contactsByBlockId?: Map<string, TouchFlowContact[]> | null;
  /** Whether the contacts overlay is on (drives the toggle's active state). */
  showContacts?: boolean;
  /** Toggle the contacts overlay. Omit to hide the toggle entirely (contact page / embedded). */
  onToggleContacts?: () => void;
  /** Total enrolled contacts, shown on the toggle when the overlay is on. */
  contactsCount?: number | null;
  /** The contacts query is in flight — spins the toggle. */
  contactsLoading?: boolean;
  /**
   * Drop the non-passive `wheel` listener so mouse-wheel / trackpad scroll bubbles
   * to the host page instead of panning the canvas. The graph stays drag-pannable.
   * Used where the canvas lives inside a scrolling surface (the contact Interact tab).
   */
  disableWheelPan?: boolean;
}

/**
 * Infinite pannable / zoomable canvas hosting the touch sequence as HTML node
 * cards, with an SVG overlay for the spine edges. Editing happens *inline* — the
 * editor card renders on the canvas at the touch's position (the view snaps to
 * 100% and centers on it). Pan by dragging empty space or scrolling; zoom with
 * ⌘/Ctrl-scroll or the zoom controls. Mirrors the translate/scale model of the
 * superadmin `Graph` canvas.
 */
export function TouchGraphCanvas({
  model,
  selectedStepId,
  onSelectStep,
  onDeleteBlock,
  onAddStep,
  onAddNoBranchStep,
  onAddForkArm,
  onAddArmStep,
  onAddTouch,
  onInsertBefore,
  onChangeWait,
  onChangeAutoWithdraw,
  addLabel,
  inlineEditor,
  candidates,
  embedded = false,
  minimal = false,
  onMarkStepFinished,
  onSetMinimal,
  showViewToggle = false,
  disableWheelPan = false,
  contactsByBlockId = null,
  showContacts = false,
  onToggleContacts,
  contactsCount = null,
  contactsLoading = false,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [waitEditBlockId, setWaitEditBlockId] = useState<string | null>(null);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  // Mirror committed state so the native (non-passive) wheel handler and the
  // pan pointer handlers can read the latest values without re-subscribing.
  const scaleRef = useRef(scale);
  scaleRef.current = scale;
  const translateRef = useRef(translate);
  translateRef.current = translate;

  const panRef = useRef<{ pointerId: number; startX: number; startY: number; originX: number; originY: number } | null>(
    null,
  );
  const [isPanning, setIsPanning] = useState(false);
  // Live viewport width — drives clamping the inline editor to fit narrow panels.
  const [viewportWidth, setViewportWidth] = useState(0);
  const didInit = useRef(false);
  const lastFocusedKey = useRef<string | null>(null);

  const { nodes, noStubs, armAddNodes, addNode, contentW, contentH } = model;
  const candidatesRect = model.candidates;

  // Opening an inline editor scrolls it into view only if it isn't already —
  // panning the minimum needed. It does NOT re-center or change the zoom, so the
  // view stays put when the editor is already visible.
  const editorKey = inlineEditor?.key ?? null;
  const editorCx = inlineEditor?.cx ?? 0;
  const editorY = inlineEditor?.y ?? 0;
  const editorW = inlineEditor?.w ?? 0;
  // Clamp the editor to the viewport (minus a margin, divided by scale since it
  // lives in content space) so it fits narrow panels — the contact side-panel is
  // far narrower than the 400px template-page editor and was overflowing. Skipped
  // when embedded (that surface intentionally lets the transient editor overflow)
  // or before the width is measured.
  const editorFitW =
    inlineEditor == null || embedded || viewportWidth === 0
      ? editorW
      : Math.min(editorW, Math.max(EDITOR_MIN_W, (viewportWidth - 2 * EDITOR_FIT_MARGIN) / scale));
  useEffect(() => {
    if (embedded) return; // static embed — no viewport, nothing to scroll into view
    if (editorKey == null) {
      lastFocusedKey.current = null;
      return;
    }
    if (lastFocusedKey.current === editorKey) return;
    lastFocusedKey.current = editorKey;
    const el = containerRef.current;
    if (el == null) return;
    const { width, height } = el.getBoundingClientRect();
    const s = scaleRef.current;
    const t = translateRef.current;
    const margin = 24;
    const estH = 320; // enough of the editor to keep on-screen
    const left = t.x + (editorCx - editorFitW / 2) * s;
    const right = t.x + (editorCx + editorFitW / 2) * s;
    const top = t.y + editorY * s;
    const bottom = top + estH * s;
    let dx = 0;
    let dy = 0;
    if (left < margin) dx = margin - left;
    else if (right > width - margin) dx = width - margin - right;
    if (top < margin) dy = margin - top;
    else if (bottom > height - margin) dy = height - margin - bottom;
    // Never push the top above the margin (editor taller than the viewport).
    if (top + dy < margin) dy = margin - top;
    if (dx !== 0 || dy !== 0) setTranslate({ x: t.x + dx, y: t.y + dy });
  }, [editorKey, editorCx, editorY, editorFitW, embedded]);

  // A branching graph has columns to the LEFT (not-accepted) and/or RIGHT (fork
  // arms) of the main spine. Centering the whole bounding box then parks the spine
  // mid-viewport and flares those columns off the right edge — so left-align it
  // instead, keeping the spine near the left with room for its branches. A plain
  // single-column campaign has no branches, so it still centers.
  const hasBranching = nodes.some((n) => n.branch !== 'main') || noStubs.length > 0 || armAddNodes.length > 0;

  // Position the content horizontally and top-align it the first time we know the
  // viewport size. Not a "fit" — the user sees the first touches at full size
  // and pans down, which reads better than shrinking a long campaign to fit.
  useEffect(() => {
    if (embedded || didInit.current) return; // embed renders at a fixed natural offset
    const el = containerRef.current;
    if (el == null) return;
    const { width } = el.getBoundingClientRect();
    if (width === 0) return;
    didInit.current = true;
    const x = hasBranching ? 24 : Math.max(24, (width - contentW) / 2);
    setTranslate({ x, y: 24 });
  }, [contentW, embedded, hasBranching]);

  // Keep `viewportWidth` in sync with the container so the inline editor re-clamps
  // when the panel resizes (e.g. the contact side-panel opening / the window).
  useEffect(() => {
    const el = containerRef.current;
    if (el == null) return;
    const measure = () => setViewportWidth(el.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const zoomAround = useCallback((factor: number, cx: number, cy: number) => {
    const prev = scaleRef.current;
    const next = clamp(prev * factor, MIN_SCALE, MAX_SCALE);
    if (next === prev) return;
    const ratio = next / prev;
    const t = translateRef.current;
    setTranslate({ x: cx - (cx - t.x) * ratio, y: cy - (cy - t.y) * ratio });
    setScale(next);
  }, []);

  // Native, non-passive wheel listener so we can preventDefault the page scroll
  // while panning/zooming the canvas.
  useEffect(() => {
    if (embedded) return; // no pan/zoom when embedded — the host surface owns scrolling
    if (disableWheelPan) return; // drag-only: let the wheel scroll the host page
    const el = containerRef.current;
    if (el == null) return;
    const onWheel = (e: WheelEvent) => {
      // Let the inline editor scroll its own content natively.
      if ((e.target as HTMLElement).closest('[data-editor]') != null) return;
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      if (e.ctrlKey || e.metaKey) {
        const factor = e.deltaY > 0 ? 0.9 : 1.1;
        zoomAround(factor, e.clientX - rect.left, e.clientY - rect.top);
        return;
      }
      setTranslate((t) => ({ x: t.x - e.deltaX, y: t.y - e.deltaY }));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomAround, embedded, disableWheelPan]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Only pan from empty canvas — never when the press lands on a node card, the
    // inline editor, or any control (add / insert / zoom buttons own their clicks).
    if ((e.target as HTMLElement).closest('[data-node],[data-control],[data-editor],button,input,textarea') != null)
      return;
    if (e.button !== 0) return;
    panRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originX: translateRef.current.x,
      originY: translateRef.current.y,
    };
    setIsPanning(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const pan = panRef.current;
    if (pan == null || pan.pointerId !== e.pointerId) return;
    setTranslate({ x: pan.originX + (e.clientX - pan.startX), y: pan.originY + (e.clientY - pan.startY) });
  };

  const endPan = (e: React.PointerEvent<HTMLDivElement>) => {
    if (panRef.current?.pointerId !== e.pointerId) return;
    panRef.current = null;
    setIsPanning(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const fit = () => {
    const el = containerRef.current;
    if (el == null) return;
    const { width, height } = el.getBoundingClientRect();
    const next = clamp(Math.min(width / contentW, height / contentH, 1), MIN_SCALE, MAX_SCALE);
    setScale(next);
    setTranslate({ x: (width - contentW * next) / 2, y: Math.max(16, (height - contentH * next) / 2) });
  };

  // Edges are parent-based (not array order) so branches work. A same-column
  // edge is a vertical connector (with the wait chip + insert "+"); a cross-column
  // edge is the "not accepted" branch drawn as a horizontal elbow.
  const nodeByBlockId = new Map(nodes.map((n) => [n.blockId, n]));
  // `prevBottom` = parent CARD bottom (where the connector emerges); `regionBottom` =
  // below the parent's below-card region (0-height now, so it coincides with the
  // card bottom — the wait chip sits in the gap beneath it).
  const segments: {
    node: (typeof nodes)[number];
    cx: number;
    prevBottom: number;
    regionBottom: number;
    top: number;
  }[] = [];
  const branchEdges: { node: (typeof nodes)[number]; parent: (typeof nodes)[number] }[] = [];
  for (const node of nodes) {
    if (node.parentBlockId == null) continue;
    const parent = nodeByBlockId.get(node.parentBlockId);
    if (parent == null) continue;
    if (parent.x === node.x) {
      segments.push({
        node,
        cx: node.x + node.w / 2,
        prevBottom: parent.y + parent.h,
        regionBottom: parent.y + parent.h + parent.belowRegionH,
        top: node.y,
      });
    } else branchEdges.push({ node, parent });
  }
  const mainNodes = nodes.filter((n) => n.branch === 'main');
  const lastMainNode = mainNodes[mainNodes.length - 1];
  const addCx = addNode.x + addNode.w / 2;
  // A touch containing a switch_campaign step is terminal — the contact moves to another
  // campaign, so nothing runs after it. Hide the trailing "add a touch / step" affordance
  // (and its connector) that would append after such a leaf, on the main spine and arms.
  const touchIsTerminal = (blockId?: string) => {
    const node = blockId != null ? nodeByBlockId.get(blockId) : undefined;
    return node != null && touchStepsAreTerminal(node.steps);
  };
  const mainSpineTerminal = touchIsTerminal(lastMainNode?.blockId);
  // How many parallel fork arms each touch already has, so the "+ branch" affordance
  // stays available until a touch hits MAX_FORK_ARMS. Precomputed once so the segment
  // map doesn't re-scan every node.
  const forkArmCountByParent = new Map<string, number>();
  for (const n of nodes) {
    if (n.edgeKind !== 'fork' || n.parentBlockId == null) continue;
    forkArmCountByParent.set(n.parentBlockId, (forkArmCountByParent.get(n.parentBlockId) ?? 0) + 1);
  }

  // Embedded: render at natural size with a fixed offset that trims the canvas's
  // outer padding.
  const embedOffset = EMBED_MARGIN - TOUCH_CANVAS_PAD;
  const { w: embedWidth, h: embedHeight } = embeddedTouchGraphSize(model);
  const contentTransform = embedded
    ? `translate(${embedOffset}px, ${embedOffset}px) scale(1)`
    : `translate(${translate.x}px, ${translate.y}px) scale(${scale})`;

  return (
    <div
      ref={containerRef}
      data-testid="touch-graph-canvas"
      onPointerDown={embedded ? undefined : onPointerDown}
      onPointerMove={embedded ? undefined : onPointerMove}
      onPointerUp={embedded ? undefined : endPan}
      onPointerCancel={embedded ? undefined : endPan}
      className={cn(
        'group/canvas relative touch-none select-none',
        // Standalone: clip to the panning viewport. Embedded: let the transient
        // inline editor (wider than a card) overflow the box instead of clipping.
        !embedded &&
          'size-full overflow-hidden bg-slate-50 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] bg-size-[24px_24px]',
        !embedded && 'dark:bg-neutral-900 dark:bg-[radial-gradient(#334155_1px,transparent_1px)]',
        !embedded && (isPanning ? 'cursor-grabbing' : 'cursor-grab'),
        embedded && 'overflow-visible',
      )}
      style={embedded ? { width: embedWidth, height: embedHeight } : undefined}>
      <div className="absolute left-0 top-0 origin-top-left" style={{ transform: contentTransform }}>
        <svg
          width={contentW}
          height={contentH}
          className="pointer-events-none absolute left-0 top-0 overflow-visible"
          viewBox={`0 0 ${contentW} ${contentH}`}>
          <defs>
            <marker id="tg-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" className="fill-lynch-300 dark:fill-slate-600" />
            </marker>
            <marker
              id="tg-arrow-green"
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" className="fill-mantis-400 dark:fill-mantis-500" />
            </marker>
            <marker
              id="tg-arrow-red"
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" className="fill-fair-pink-400 dark:fill-fair-pink-500" />
            </marker>
            <marker
              id="tg-arrow-blue"
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="6"
              markerHeight="6"
              orient="auto">
              <path d="M 0 0 L 10 5 L 0 10 z" className="fill-blue-400 dark:fill-blue-500" />
            </marker>
          </defs>
          {/* Same-column connector, broken around the +/wait stack. The accepted
              fork arm is green. */}
          {segments.map(({ node, cx, top, prevBottom, regionBottom }) => {
            // The gray line runs straight from the card bottom to the next touch; the
            // wait-chip break sits centered in the gap between them.
            const midY = (regionBottom + top) / 2;
            const accepted = node.edgeKind === 'accepted';
            return (
              <g
                key={`edge-${node.blockId}`}
                className={
                  accepted ? 'stroke-mantis-400 dark:stroke-mantis-500/80' : 'stroke-lynch-300 dark:stroke-slate-600'
                }
                strokeWidth={2}>
                <line x1={cx} y1={prevBottom} x2={cx} y2={midY - EDGE_GAP_HALF} />
                <line
                  x1={cx}
                  y1={midY + EDGE_GAP_HALF}
                  x2={cx}
                  y2={top - ARROW_INSET}
                  markerEnd={accepted ? 'url(#tg-arrow-green)' : 'url(#tg-arrow)'}
                />
              </g>
            );
          })}
          {/* Cross-column arms: the not-accepted (No) branch elbows down-LEFT (red,
              dashed); a parallel fork arm elbows down-RIGHT (blue, solid). Both share
              the generic parent-based edge; the edgeKind picks the styling. */}
          {branchEdges.map(({ node, parent }) => {
            const isFork = node.edgeKind === 'fork';
            return (
              <path
                key={`branch-${node.blockId}`}
                d={forkPath(parent, node.x + node.w / 2, node.y)}
                fill="none"
                className={
                  isFork
                    ? 'stroke-blue-400 dark:stroke-blue-500/70'
                    : 'stroke-fair-pink-400 dark:stroke-fair-pink-500/70'
                }
                strokeWidth={2}
                strokeDasharray={isFork ? undefined : '4 5'}
                markerEnd={isFork ? 'url(#tg-arrow-blue)' : 'url(#tg-arrow-red)'}
              />
            );
          })}
          {/* Not-accepted arm into each empty-No stub. */}
          {noStubs.map((stub) => {
            const parent = nodeByBlockId.get(stub.connectBlockId);
            if (parent == null) return null;
            return (
              <path
                key={`nostub-edge-${stub.connectBlockId}`}
                d={forkPath(parent, stub.x + stub.w / 2, stub.y)}
                fill="none"
                className="stroke-fair-pink-300 dark:stroke-fair-pink-500/60"
                strokeWidth={2}
                strokeDasharray="4 5"
                markerEnd="url(#tg-arrow-red)"
              />
            );
          })}
          {lastMainNode != null && !mainSpineTerminal && (
            <line
              x1={addCx}
              y1={lastMainNode.y + lastMainNode.h}
              x2={addCx}
              y2={addNode.y - ARROW_INSET}
              className="stroke-lynch-200 dark:stroke-slate-700"
              strokeWidth={2}
              strokeDasharray="4 5"
              markerEnd="url(#tg-arrow)"
            />
          )}
          {/* Each fork arm's tail → its own trailing add-node (same dashed connector
              as the main spine's add-node). */}
          {armAddNodes.map((a) => {
            const tail = nodeByBlockId.get(a.tailBlockId);
            if (tail == null || touchIsTerminal(a.tailBlockId)) return null;
            const cx = a.x + a.w / 2;
            return (
              <line
                key={`arm-add-edge-${a.tailBlockId}`}
                x1={cx}
                y1={tail.y + tail.h}
                x2={cx}
                y2={a.y - ARROW_INSET}
                className="stroke-lynch-200 dark:stroke-slate-700"
                strokeWidth={2}
                strokeDasharray="4 5"
                markerEnd="url(#tg-arrow)"
              />
            );
          })}
          {/* Candidates header → first touch. */}
          {candidatesRect != null &&
            (() => {
              const firstEl = mainNodes[0] ?? addNode;
              return (
                <line
                  x1={candidatesRect.x + candidatesRect.w / 2}
                  y1={candidatesRect.y + candidatesRect.h}
                  x2={firstEl.x + firstEl.w / 2}
                  y2={firstEl.y - ARROW_INSET}
                  className="stroke-lynch-300 dark:stroke-slate-600"
                  strokeWidth={2}
                  markerEnd="url(#tg-arrow)"
                />
              );
            })()}
        </svg>

        {/* Nodes — the one being edited is replaced by its inline editor below. */}
        {nodes.map((node) => {
          if (node.blockId === inlineEditor?.hideBlockId) return null;
          return (
            <div
              key={node.blockId}
              className="group/row absolute isolate"
              style={{ left: node.x, top: node.y, width: node.w, height: node.h }}>
              <TouchNodeCard
                node={node}
                minimal={minimal}
                selectedStepId={selectedStepId}
                onSelectStep={onSelectStep}
                onRequestDelete={() => onDeleteBlock(node.blockId)}
                onAddStepToGroup={onAddStep != null ? () => onAddStep(node.blockId, node.lastStepId) : undefined}
                onMarkStepFinished={onMarkStepFinished}
              />

              {/* Insert a touch ABOVE the first one. Center the "+" on the connector
                  from the candidates header (when present) down to this touch;
                  otherwise float it just above the touch. */}
              {node.branch === 'main' && node.parentBlockId == null && (
                <div
                  data-control="insert"
                  className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 transition-opacity group-focus-within/row:opacity-100 group-hover/row:opacity-100"
                  style={{ top: candidatesRect != null ? (candidatesRect.y + candidatesRect.h - node.y) / 2 : -22 }}>
                  <Button
                    data-testid={`touch-insert-before-${node.blockId}`}
                    icon={<General.Plus className="size-3.5" />}
                    size="2xs"
                    tone="soft"
                    tooltip="Add a step above"
                    onClick={() => onInsertBefore(node.blockId)}
                    className="rounded-full shadow-sm"
                  />
                </div>
              )}

              {/* Branching lives on the connector below the touch (the git-branch icon
                  beside the insert "+"), not as a separate card-side button. */}
            </div>
          );
        })}

        {/* Enrolled-contact face piles — the "show contacts in this flow" overlay,
            tucked into the gap on the right of each touch (fits the arm-column gap).
            Lives inside the transform so faces pan / zoom with their card. */}
        {contactsByBlockId != null &&
          nodes.map((node) => {
            if (node.blockId === inlineEditor?.hideBlockId) return null;
            const contacts = contactsByBlockId.get(node.blockId);
            if (contacts == null || contacts.length === 0) return null;
            return (
              <div
                key={`faces-${node.blockId}`}
                data-testid={`touch-faces-${node.blockId}`}
                className="absolute"
                style={{ left: node.x + node.w + FACE_GAP, top: node.y }}>
                <ContactFacePile contacts={contacts} />
              </div>
            );
          })}

        {/* Each node-to-node edge stacks the insert "+" ABOVE the wait chip,
            centered on the spine. The wait chip is click-to-edit. */}
        {segments.map(({ node, cx, top, regionBottom }) => {
          const editingWait = waitEditBlockId === node.blockId;
          // A branch icon beside the connector "+" forks a parallel arm from the touch
          // ABOVE this connector (the group that just finished). Offered on main-spine
          // connectors whose parent touch has steps and still has a free arm slot — you
          // can keep adding arms up to MAX_FORK_ARMS from the same point.
          const branchParent = node.parentBlockId != null ? nodeByBlockId.get(node.parentBlockId) : undefined;
          const branchFromId =
            onAddForkArm != null &&
            branchParent != null &&
            branchParent.branch === 'main' &&
            branchParent.steps.length > 0 &&
            (forkArmCountByParent.get(branchParent.blockId) ?? 0) < MAX_FORK_ARMS
              ? branchParent.blockId
              : null;
          // The hover band spans the gap between the parent card and the next touch,
          // so the wait chip + insert "+" sit centered in that gap.
          return (
            <div
              key={`gap-${node.blockId}`}
              data-testid={`touch-edge-${node.blockId}`}
              className="group/edge absolute"
              style={{
                left: cx - EDGE_HOVER_W / 2,
                top: regionBottom,
                width: EDGE_HOVER_W,
                height: top - regionBottom,
              }}>
              {editingWait && (
                <div data-editor="wait" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                  <TemplateBlockDelayEditPure
                    initialDays={Math.max(0, Math.round(node.delayMs / DAY_MS))}
                    initialAlignWeek={node.alignWeek}
                    onSubmit={async (days, alignWeek) => {
                      setWaitEditBlockId(null);
                      await onChangeWait(node.blockId, days, alignWeek);
                    }}
                    onCancel={() => setWaitEditBlockId(null)}
                  />
                </div>
              )}
              {!editingWait && (
                // Wait chip centered on the connector; the "+" floats just above
                // it (no layout impact), revealed on hover of the whole band.
                <div className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-0.5">
                  <div
                    data-control="insert"
                    className="absolute bottom-full left-1/2 mb-1 -translate-x-1/2 opacity-0 transition-opacity group-focus-within/edge:opacity-100 group-hover/edge:opacity-100">
                    <Button
                      data-testid={`touch-insert-before-${node.blockId}`}
                      icon={<General.Plus className="size-3.5" />}
                      size="2xs"
                      tone="soft"
                      tooltip="Insert a touch here"
                      onClick={() => onInsertBefore(node.blockId)}
                      className="rounded-full shadow-sm"
                    />
                    {/* Branch icon to the RIGHT of the "+": forks a parallel arm from the
                        touch above this connector (same as its "+ branch" button). */}
                    {branchFromId != null && onAddForkArm != null && (
                      <Button
                        data-testid={`touch-branch-at-${node.blockId}`}
                        icon={<Development.GitBranch01 className="size-3.5" />}
                        size="2xs"
                        tone="soft"
                        color="blue"
                        tooltip="After the previous step group finishes, start a new branch of steps"
                        onClick={() => onAddForkArm(branchFromId)}
                        className="absolute left-full top-1/2 ml-1 -translate-y-1/2 rounded-full shadow-sm"
                      />
                    )}
                  </div>
                  {node.edgeKind === 'accepted' && (
                    <span
                      data-testid={`touch-accepted-${node.blockId}`}
                      className="whitespace-nowrap rounded-full bg-mantis-100 px-1.5 py-0.5 text-[10px] font-semibold text-mantis-700 dark:bg-mantis-900 dark:text-mantis-200">
                      accepted
                    </span>
                  )}
                  {node.waitLabel != null && (
                    // One chip, one style — while an inline editor is open it becomes
                    // read-only (non-clickable) instead of switching to a different
                    // static-pill style, so the wait chips never visibly change.
                    <Button
                      data-control="wait"
                      data-testid={`touch-wait-${node.blockId}`}
                      size="2xs"
                      tone="soft"
                      icon={<Time.Clock className="size-3" />}
                      tooltip={inlineEditor == null ? 'Edit the wait' : undefined}
                      readOnly={inlineEditor != null}
                      onClick={inlineEditor == null ? () => setWaitEditBlockId(node.blockId) : undefined}
                      className="whitespace-nowrap rounded-full shadow-sm">
                      {node.waitLabel}
                    </Button>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Fork elbow: the "not accepted" label + an editable "wait for
            acceptance" (the wait_for_connect's own delay). */}
        {nodes.map((connect) => {
          const wfcId = connect.wfcId;
          if (!connect.waitsForConnect || wfcId == null || inlineEditor?.hideBlockId === connect.blockId) return null;
          const noNode = nodes.find((n) => n.edgeKind === 'notAccepted' && n.parentBlockId === connect.blockId);
          const stub = noStubs.find((s) => s.connectBlockId === connect.blockId);
          const redCx = noNode != null ? noNode.x + noNode.w / 2 : stub != null ? stub.x + stub.w / 2 : connect.x;
          const forkY = connect.y + connect.h + FORK_STEM;
          const labelX = (connect.x + FORK_LEFT_EXIT + redCx) / 2;
          const editing = waitEditBlockId === wfcId;
          const waitLabel = formatWaitLabel(connect.wfcDelayMs, connect.wfcAlignWeek);
          // The connect step owns the auto-withdraw flag; it always lives in this
          // waiting touch's step group (delay / wait_for_connect are filtered out).
          const connectStep = connect.steps.find((s) => s.type === 'connect');
          return (
            <div
              key={`fork-${connect.blockId}`}
              className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-0.5"
              style={{ left: labelX, top: forkY }}>
              {editing && (
                <div data-editor="wait">
                  <TemplateBlockDelayEditPure
                    initialDays={Math.max(0, Math.round(connect.wfcDelayMs / DAY_MS))}
                    initialAlignWeek={connect.wfcAlignWeek}
                    onSubmit={async (days, alignWeek) => {
                      setWaitEditBlockId(null);
                      await onChangeWait(wfcId, days, alignWeek);
                    }}
                    onCancel={() => setWaitEditBlockId(null)}
                  />
                </div>
              )}
              {!editing && (
                <>
                  <span className="whitespace-nowrap rounded-full bg-fair-pink-100 px-1.5 py-0.5 text-[10px] font-semibold text-fair-pink-700 dark:bg-fair-pink-950 dark:text-fair-pink-300">
                    not accepted
                  </span>
                  <Button
                    data-control="wait"
                    data-testid={`touch-wfc-wait-${connect.blockId}`}
                    size="2xs"
                    tone="soft"
                    icon={<Time.Clock className="size-3" />}
                    tooltip="How long to wait for acceptance before the not-accepted path runs"
                    onClick={() => setWaitEditBlockId(wfcId)}
                    className="whitespace-nowrap rounded-full shadow-sm">
                    {waitLabel != null ? `after ${waitLabel}` : 'set wait'}
                  </Button>
                  {connectStep != null && (
                    <TouchAutoWithdrawChip
                      blockId={connect.blockId}
                      checked={connectStep.autoWithdraw}
                      onChange={
                        onChangeAutoWithdraw != null
                          ? (value) => void onChangeAutoWithdraw(connectStep.id, value)
                          : undefined
                      }
                    />
                  )}
                </>
              )}
            </div>
          );
        })}

        {/* Empty not-accepted branch → a "+ if not accepted" affordance. */}
        {noStubs.map((stub) => (
          <div
            key={`nostub-${stub.connectBlockId}`}
            data-control="nostub"
            className={cn('absolute', minimal && 'flex items-center justify-center')}
            style={{ left: stub.x, top: stub.y, width: stub.w, height: stub.h }}>
            {/* Minimal → the shared "+" tile (label in tooltip) so it matches every
                other compact add tile; full mode keeps the pink labelled affordance
                that signals the not-accepted branch when there's room for the text. */}
            {minimal && (
              <MinimalAddTile
                testId={`touch-nobranch-add-${stub.connectBlockId}`}
                tooltip="Add a not-accepted step"
                onClick={() => onAddNoBranchStep(stub.connectBlockId, stub.wfcId)}
              />
            )}
            {!minimal && (
              <Button
                data-testid={`touch-nobranch-add-${stub.connectBlockId}`}
                text
                textAlign="center"
                icon={<General.Plus className="size-3.5" />}
                onClick={() => onAddNoBranchStep(stub.connectBlockId, stub.wfcId)}
                className={cn(
                  'size-full justify-center gap-1 rounded-xl border border-dashed text-xs font-medium',
                  'border-fair-pink-300 text-fair-pink-500 hover:border-fair-pink-400 hover:bg-fair-pink-50/70 hover:text-fair-pink-600',
                  'dark:border-fair-pink-500/40 dark:text-fair-pink-300/90 dark:hover:bg-fair-pink-950/40',
                )}>
                Add a not-accepted step
              </Button>
            )}
          </div>
        ))}

        {/* Trailing add-node — hidden after a terminal switch_campaign leaf. */}
        {!inlineEditor?.hideAdd && !mainSpineTerminal && (
          <div
            data-control="add"
            className={cn('absolute', minimal && 'flex items-center justify-center')}
            style={{ left: addNode.x, top: addNode.y, width: addNode.w, height: addNode.h }}>
            {/* Minimal cards hug ~96px, too narrow for the full label — use the shared
                dashed "+" tile (identical to the add-step chip) with the label in a
                tooltip. Full mode keeps the wide labelled button. */}
            {minimal && <MinimalAddTile testId="touch-add-node" tooltip={addLabel} onClick={onAddTouch} />}
            {!minimal && (
              <Button
                data-testid="touch-add-node"
                text
                textAlign="center"
                onClick={onAddTouch}
                icon={<General.Plus className="size-3.5" />}
                className={cn(
                  'size-full justify-center rounded-xl border-2 border-dashed',
                  'border-lynch-300 text-lynch-600 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700',
                  'dark:border-slate-700 dark:text-lynch-300 dark:hover:border-blue-400 dark:hover:bg-blue-950 dark:hover:text-blue-300',
                )}>
                {addLabel}
              </Button>
            )}
          </div>
        )}

        {/* Branch-start wait: the delay from the fork touch's group finishing to the
            branch's first step — editable, exactly like a normal wait chip. Sits on
            the blue fork elbow. */}
        {branchEdges
          .filter(({ node }) => node.edgeKind === 'fork')
          .map(({ node, parent }) => {
            const editing = waitEditBlockId === node.blockId;
            const armCx = node.x + node.w / 2;
            const parentExit = parent.x + parent.w - FORK_LEFT_EXIT;
            const forkY = parent.y + parent.h + FORK_STEM;
            const labelX = (parentExit + armCx) / 2;
            return (
              <div
                key={`arm-fork-wait-${node.blockId}`}
                className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-0.5"
                style={{ left: labelX, top: forkY }}>
                {editing && (
                  <div data-editor="wait">
                    <TemplateBlockDelayEditPure
                      initialDays={Math.max(0, Math.round(node.delayMs / DAY_MS))}
                      initialAlignWeek={node.alignWeek}
                      onSubmit={async (days, alignWeek) => {
                        setWaitEditBlockId(null);
                        await onChangeWait(node.blockId, days, alignWeek);
                      }}
                      onCancel={() => setWaitEditBlockId(null)}
                    />
                  </div>
                )}
                {/* Always show a chip on the branch start: the wait when there is one,
                    else "runs right away" to make the auto-run explicit (a parallel arm
                    fires the moment the group finishes). Click to add/edit the wait. */}
                {!editing && (
                  <Button
                    data-control="wait"
                    data-testid={`touch-wait-${node.blockId}`}
                    size="2xs"
                    tone="soft"
                    icon={
                      node.waitLabel != null ? <Time.Clock className="size-3" /> : <General.Zap className="size-3" />
                    }
                    tooltip={
                      inlineEditor == null ? (node.waitLabel != null ? 'Edit the wait' : 'Add a wait') : undefined
                    }
                    readOnly={inlineEditor != null}
                    onClick={inlineEditor == null ? () => setWaitEditBlockId(node.blockId) : undefined}
                    className="whitespace-nowrap rounded-full shadow-sm">
                    {node.waitLabel ?? 'runs right away'}
                  </Button>
                )}
              </div>
            );
          })}

        {/* Each fork arm's trailing "Add a step" node — the arm's own version of the
            main add-node, appending a day-delayed step onto the arm's tail. Stays
            visible while other editors are open; only the arm being appended-to hides
            (its editor replaces it). */}
        {onAddArmStep != null &&
          armAddNodes
            .filter((a) => a.tailBlockId !== inlineEditor?.hideArmTailBlockId && !touchIsTerminal(a.tailBlockId))
            .map((a) => (
              <div
                key={`arm-add-${a.tailBlockId}`}
                data-control="add-arm"
                className={cn('absolute', minimal && 'flex items-center justify-center')}
                style={{ left: a.x, top: a.y, width: a.w, height: a.h }}>
                {minimal && (
                  <MinimalAddTile
                    testId={`touch-add-arm-step-${a.tailBlockId}`}
                    tooltip="Add a step"
                    onClick={() => onAddArmStep(a.tailBlockId)}
                  />
                )}
                {!minimal && (
                  <Button
                    data-testid={`touch-add-arm-step-${a.tailBlockId}`}
                    text
                    textAlign="center"
                    onClick={() => onAddArmStep(a.tailBlockId)}
                    icon={<General.Plus className="size-3.5" />}
                    className={cn(
                      'size-full justify-center rounded-xl border-2 border-dashed',
                      'border-lynch-300 text-lynch-600 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-700',
                      'dark:border-slate-700 dark:text-lynch-300 dark:hover:border-blue-400 dark:hover:bg-blue-950 dark:hover:text-blue-300',
                    )}>
                    Add a step
                  </Button>
                )}
              </div>
            ))}

        {/* Candidates → enrollment pipeline, above the first touch. */}
        {candidates != null && candidatesRect != null && (
          <div
            className="absolute"
            style={{
              left: candidatesRect.x,
              top: candidatesRect.y,
              width: candidatesRect.w,
              height: candidatesRect.h,
            }}>
            {!candidates.expanded && (
              <div
                role="button"
                tabIndex={0}
                data-node="candidates"
                data-testid="candidates-node"
                onClick={candidates.onToggle}
                className="flex size-full cursor-pointer items-center gap-3 rounded-xl border border-lynch-200 bg-white px-4 shadow-sm transition-colors hover:border-blue-300 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-blue-500/60">
                <Development.Dataflow03 className="size-5 shrink-0 text-blue-500 dark:text-blue-300" />
                <div className="flex min-w-0 flex-col">
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">Candidates</span>
                  <span className="text-xs text-lynch-500 dark:text-lynch-400">
                    {candidates.total} in the enrollment pipeline
                  </span>
                </div>
                {candidates.errorCount > 0 && (
                  <span
                    data-testid="candidates-error"
                    className="ml-auto shrink-0 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300">
                    {candidates.errorCount} errored
                  </span>
                )}
                <General.Plus
                  className={cn('size-4 shrink-0 text-lynch-400', candidates.errorCount > 0 ? '' : 'ml-auto')}
                />
              </div>
            )}
            {candidates.expanded && (
              <div
                data-editor="candidates"
                data-testid="candidates-flow"
                className="relative size-full overflow-hidden rounded-xl border border-lynch-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
                <Button
                  data-testid="candidates-collapse"
                  icon={<General.XMark />}
                  text
                  size="xs"
                  tone="soft"
                  tooltip="Collapse"
                  onClick={candidates.onToggle}
                  className="absolute right-2 top-2 z-10"
                />
                <CandidateFlowGraph
                  counts={candidates.counts}
                  hasFilterRules={candidates.hasFilterRules}
                  hasPersonalizedSteps={candidates.hasPersonalizedSteps}
                />
              </div>
            )}
          </div>
        )}

        {/* Inline editor — rendered on the canvas at the touch's position. */}
        {inlineEditor != null && (
          <div
            data-editor="touch"
            data-testid="touch-inline-editor"
            className="absolute z-30"
            style={{ left: inlineEditor.cx - editorFitW / 2, top: inlineEditor.y, width: editorFitW }}>
            {inlineEditor.children}
          </div>
        )}
      </div>

      {/* Zoom controls — fixed to the viewport (outside the transform). Hidden when embedded. */}
      {!embedded && (
        <ButtonGroup className="absolute bottom-3 left-1/2 -translate-x-1/2">
          <Button
            data-control="zoom-out"
            icon={<General.Minus />}
            tooltip="Zoom out"
            onClick={() => setScale((s) => clamp(s / 1.2, MIN_SCALE, MAX_SCALE))}
          />
          <Button data-control="zoom-reset" onClick={() => setScale(1)} tooltip="Reset zoom">
            {Math.round(scale * 100)}%
          </Button>
          <Button
            data-control="zoom-in"
            icon={<General.Plus />}
            tooltip="Zoom in"
            onClick={() => setScale((s) => clamp(s * 1.2, MIN_SCALE, MAX_SCALE))}
          />
          <Button data-control="zoom-fit" icon={<General.Maximize01 />} tooltip="Fit to screen" onClick={fit} />
        </ButtonGroup>
      )}

      {/* Bottom-left control stack: the "show contacts" overlay toggle above the
          Minimal / Expanded view-mode toggle. Both modes render the SAME branching
          graph — only each touch's node density changes (compact icons vs full card). */}
      {!embedded && (onToggleContacts != null || (showViewToggle && onSetMinimal != null)) && (
        <div className="absolute bottom-4 left-4 flex flex-col items-start gap-2">
          {onToggleContacts != null && (
            <Button
              data-control="toggle-contacts"
              data-testid="touch-toggle-contacts"
              size="xs"
              color={showContacts ? 'blue' : 'gray'}
              tone={showContacts ? 'filled' : 'outline'}
              icon={<Users.Users01 />}
              status={contactsLoading ? 'updating' : undefined}
              tooltip={showContacts ? 'Hide enrolled contacts' : 'Show where enrolled contacts are in this flow'}
              onClick={onToggleContacts}>
              {showContacts && contactsCount != null ? `Contacts · ${contactsCount}` : 'Contacts'}
            </Button>
          )}
          {showViewToggle && onSetMinimal != null && (
            <ButtonGroup data-control="view-mode">
              <Button
                data-control="view-minimal"
                size="xs"
                color={minimal ? 'blue' : 'gray'}
                tone={minimal ? 'filled' : 'outline'}
                icon={<Layout.Rows03 />}
                tooltip="Minimal — just each touch's step icons"
                onClick={() => onSetMinimal(true)}>
                Minimal
              </Button>
              <Button
                data-control="view-expanded"
                size="xs"
                color={minimal ? 'gray' : 'blue'}
                tone={minimal ? 'outline' : 'filled'}
                icon={<Layout.LayoutGrid02 />}
                tooltip="Expanded — the full touch cards"
                onClick={() => onSetMinimal(false)}>
                Expanded
              </Button>
            </ButtonGroup>
          )}
        </div>
      )}
    </div>
  );
}
